import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GridOff,
  KeyboardDoubleArrowDown,
  AttachMoney as MoneyIcon,
  Rule as RuleIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Public as PublicIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { AppBar } from 'components';
import FilterInput from 'components/FilterInput';
import { useAuth, useCommercialPlan } from 'contexts';
import { TypeUser } from 'types/enums';
import { ICommercialPlanViewModel } from 'viewModels';
import { formatCurrency } from 'utils/formatCurrency';
import CreatePlanModal from './CreatePlanModal';
import EditPlanModal from './EditPlanModal';
import DeletePlanModal from './DeletePlanModal';
import {
  CardsGrid,
  PlanCard,
  CardHeader,
  CardInfo,
  CardStats,
  CardActions as StyledCardActions,
} from './styles';

const CommercialPlanPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    commercialPlans,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    handleFetch,
    handleFetchNextPage,
  } = useCommercialPlan();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] =
    useState<ICommercialPlanViewModel | null>(null);
  const [orderBy, setOrderBy] = useState<
    Array<{ key: string; direction: 'asc' | 'desc' }>
  >([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Verificar se o usuário é admin
  const isAdmin = user?.typeUser === TypeUser.ADM;

  useEffect(() => {
    if (isAdmin) {
      handleFetch();
    }
  }, [isAdmin, handleFetch]);

  const handleSort = (key: string) => () => {
    setOrderBy(prev => {
      const existing = prev.find(o => o.key === key);
      if (existing) {
        return prev.map(o =>
          o.key === key
            ? {
              ...o,
              direction: o.direction === 'asc' ? 'desc' : 'asc',
            }
            : o
        );
      }
      return [...prev, { key, direction: 'asc' }];
    });
  };

  const sortPlans = (plans: ICommercialPlanViewModel[]) => {
    if (!orderBy.length) return plans;

    return [...plans].sort((a, b) => {
      for (const order of orderBy) {
        let aValue: any;
        let bValue: any;

        switch (order.key) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'isActive':
            aValue = a.isActive;
            bValue = b.isActive;
            break;
          case 'isDefault':
            aValue = a.isDefault;
            bValue = b.isDefault;
            break;
          case 'minimumConsumption':
            aValue = a.minimumConsumption;
            bValue = b.minimumConsumption;
            break;
          case 'userCount':
            aValue = a.userCount;
            bValue = b.userCount;
            break;
          case 'fontRuleCount':
            aValue = a.fontRuleCount;
            bValue = b.fontRuleCount;
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt).getTime();
            bValue = new Date(b.createdAt).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return order.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return order.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  };

  const handleEditPlan = (plan: ICommercialPlanViewModel) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  const handleDeletePlan = (plan: ICommercialPlanViewModel) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  const handleCardClick = (planId: string) => {
    navigate(`/commercialPlans/${planId}`);
  };

  // Função para remover acentos de uma string
  const removeAccents = (str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Função para filtrar planos baseado no termo de busca
  const filterPlans = (plans: ICommercialPlanViewModel[]) => {
    if (!searchTerm.trim()) return plans;

    const term = removeAccents(searchTerm.toLowerCase().trim());
    return plans.filter(plan => {
      const name = removeAccents(plan.name.toLowerCase());
      const description = plan.description ? removeAccents(plan.description.toLowerCase()) : '';
      const isActiveText = removeAccents(plan.isActive ? 'ativo' : 'inativo');
      const isDefaultText = removeAccents(plan.isDefault ? 'padrão' : '');

      return name.includes(term) ||
        description.includes(term) ||
        plan.minimumConsumption.toString().includes(term) ||
        plan.userCount.toString().includes(term) ||
        plan.fontRuleCount.toString().includes(term) ||
        plan.maxPublicFonts.toString().includes(term) ||
        plan.extraFontsTaxRate.toString().includes(term) ||
        isActiveText.includes(term) ||
        isDefaultText.includes(term);
    });
  };

  const filteredAndSortedPlans = sortPlans(filterPlans(commercialPlans));

  if (!isAdmin) {
    return (
      <>
        <AppBar />
        <Container maxWidth="lg" sx={{ mt: 10, mb: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Acesso negado. Apenas administradores podem acessar esta página.
            </Typography>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBar />
      <Container sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}>
        <Paper
          sx={{
            p: 4,
            mb: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          <Grid2
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Typography
              component="p"
              variant="h4"
              color="primary"
              sx={{
                fontWeight: 600,
                position: 'relative',
                marginBottom: '24px',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4,
                },
              }}
            >
              Planos Comerciais
            </Typography>
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="contained"
              aria-label="Create new commercial plan"
              startIcon={<AddIcon style={{ width: 18 }} />}
            >
              Novo Plano
            </Button>
          </Grid2>

          <FilterInput
            onInputChange={change => {
              setSearchTerm(change.term || '');
            }}
          />

          {searchTerm.trim() && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Buscando por: <strong>"{searchTerm}"</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {filteredAndSortedPlans.length} {filteredAndSortedPlans.length === 1 ? 'resultado' : 'resultados'} encontrado{filteredAndSortedPlans.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}

          {isLoading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: '300px' }}
            >
              <CircularProgress size={40} />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ ml: 2, fontStyle: 'italic' }}
              >
                Carregando planos comerciais...
              </Typography>
            </Box>
          ) : !filteredAndSortedPlans.length ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: '300px', py: 4 }}
            >
              <GridOff
                sx={{
                  fontSize: 60,
                  color: theme.palette.text.secondary,
                  mb: 2,
                }}
                aria-label="No plans icon"
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              >
                {searchTerm.trim() ? 'Nenhum plano encontrado para sua busca' : 'Nenhum plano comercial encontrado'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  color: theme.palette.text.secondary,
                }}
              >
                {searchTerm.trim()
                  ? `Tente ajustar os termos de busca ou criar um novo plano comercial.`
                  : 'Crie um novo plano comercial para começar!'
                }
              </Typography>
            </Box>
          ) : (
            <>
              <CardsGrid>
                {filteredAndSortedPlans.map((plan, idx) => (
                  <PlanCard
                    key={plan.id}
                    onClick={() => handleCardClick(plan.id)}
                  >
                    <CardHeader>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {plan.name}
                          </Typography>
                          {plan.isDefault && (
                            <StarIcon
                              sx={{
                                fontSize: 20,
                                color: theme.palette.warning.main,
                              }}
                            />
                          )}
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Editar plano">
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                              aria-label="Edit plan"
                              onClick={e => {
                                e.stopPropagation();
                                handleEditPlan(plan);
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                        <Chip
                          label={plan.isActive ? 'Ativo' : 'Inativo'}
                          size="small"
                          color={plan.isActive ? 'success' : 'error'}
                          variant="outlined"
                          icon={
                            plan.isActive ? <CheckCircleIcon /> : <CancelIcon />
                          }
                        />
                        {plan.isDefault && (
                          <Chip
                            label="Padrão"
                            size="small"
                            color="warning"
                            variant="outlined"
                            icon={<StarIcon />}
                          />
                        )}
                      </Box>
                    </CardHeader>

                    <CardInfo>
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        mb={2}
                      >
                        <DescriptionIcon
                          sx={{
                            fontSize: 16,
                            color: theme.palette.text.secondary,
                            mt: 0.2,
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            minHeight: '40px',
                            lineHeight: 1.4,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '200px',
                          }}
                        >
                          {plan.description || 'Sem descrição'}
                        </Typography>
                      </Box>
                    </CardInfo>

                    <Divider sx={{ my: 2 }} />

                    <CardStats>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={2}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <MoneyIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.success.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Consumo mínimo
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(plan.minimumConsumption)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={2}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <PersonIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.info.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Usuários ativos
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {plan.userCount}{' '}
                            {plan.userCount === 1 ? 'usuário' : 'usuários'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <RuleIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.warning.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Regras de fontes
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {plan.fontRuleCount}{' '}
                            {plan.fontRuleCount === 1 ? 'regra' : 'regras'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <PublicIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.secondary.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Máx. fontes por dossiê
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {plan.maxPublicFonts} fontes
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[50],
                        }}
                      >
                        <PercentIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.error.main,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Taxa extra por fonte
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {plan.extraFontsTaxRate}%
                          </Typography>
                        </Box>
                      </Box>
                    </CardStats>

                    <StyledCardActions>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{
                          mt: 1,
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                          },
                        }}
                      >
                        Ver Detalhes
                      </Button>
                    </StyledCardActions>
                  </PlanCard>
                ))}
              </CardsGrid>

              {isFetchingNextPage && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mt: 3 }}
                >
                  <CircularProgress size={20} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, fontStyle: 'italic' }}
                  >
                    Carregando mais planos...
                  </Typography>
                </Box>
              )}

              {hasNextPage && !isFetchingNextPage && (
                <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                  <Button
                    onClick={() => handleFetchNextPage()}
                    variant="outlined"
                    startIcon={<KeyboardDoubleArrowDown />}
                    aria-label="Load more plans"
                  >
                    Carregar mais planos
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Modals */}
        <CreatePlanModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            handleFetch();
          }}
        />

        {selectedPlan && (
          <>
            <EditPlanModal
              open={showEditModal}
              onClose={() => {
                setShowEditModal(false);
                setSelectedPlan(null);
              }}
              plan={selectedPlan}
              onSuccess={() => {
                setShowEditModal(false);
                setSelectedPlan(null);
                handleFetch();
              }}
            />

            <DeletePlanModal
              open={showDeleteModal}
              onClose={() => {
                setShowDeleteModal(false);
                setSelectedPlan(null);
              }}
              plan={selectedPlan}
              onSuccess={() => {
                setShowDeleteModal(false);
                setSelectedPlan(null);
                handleFetch();
              }}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default CommercialPlanPage;
